import type { Money, MoneyServer } from '../types'

import { formatNumberValue, type NumberNotation } from './number.core'

const currencyFormatLocale: Record<string, string> = {
  AUD: 'en-AU',
  CAD: 'en-CA',
  CHF: 'de-CH',
  CZK: 'cs-CZ',
  DKK: 'da-DK',
  EUR: 'en-GB',
  GBP: 'en-GB',
  NOK: 'nb-NO',
  PLN: 'pl-PL',
  RUB: 'ru-RU',
  SEK: 'sv-SE',
  USD: 'en-US',
}

export type MoneyFormatOptions = {
  notation?: NumberNotation
  maximumFractionDigits?: number
  minimumFractionDigits?: number
  currencyDisplay?: Intl.NumberFormatOptions['currencyDisplay']
  signDisplay?: Intl.NumberFormatOptions['signDisplay']
  /** Controls digit separators for every amount. It never controls symbol placement. */
  locale?: Intl.LocalesArgument
  /** Overrides currency symbol placement for contexts that explicitly follow the selected country. */
  currencyLocale?: Intl.LocalesArgument
  currencySymbol?: string
  isSymbolBefore?: boolean
  allowZero?: boolean
}

const currencyParts = (
  currency: string,
  currencyDisplay: Intl.NumberFormatOptions['currencyDisplay'],
  locale?: Intl.LocalesArgument,
  amount = 0,
  signDisplay?: Intl.NumberFormatOptions['signDisplay'],
  currencyLocale?: Intl.LocalesArgument,
) => {
  const symbolLocale = currencyLocale ?? currencyFormatLocale[currency] ?? locale ?? 'en-GB'
  return new Intl.NumberFormat(symbolLocale, {
    style: 'currency',
    currency,
    currencyDisplay,
    ...(signDisplay ? { signDisplay } : {}),
  }).formatToParts(amount)
}

const formatMoneyAffixes = (
  currency: string,
  currencyDisplay: Intl.NumberFormatOptions['currencyDisplay'],
  locale?: Intl.LocalesArgument,
  amount = 0,
  signDisplay?: Intl.NumberFormatOptions['signDisplay'],
  currencyLocale?: Intl.LocalesArgument,
) => {
  const parts = currencyParts(
    currency,
    currencyDisplay,
    locale,
    amount,
    signDisplay,
    currencyLocale,
  )
  const firstNumberPart = parts.findIndex((part) => part.type === 'integer')
  const lastNumberPart = parts.reduce(
    (lastIndex, part, index) =>
      part.type === 'fraction' || part.type === 'integer' ? index : lastIndex,
    -1,
  )

  return {
    prefix: parts
      .slice(0, firstNumberPart)
      .map((part) => part.value)
      .join(''),
    suffix: parts
      .slice(lastNumberPart + 1)
      .map((part) => part.value)
      .join(''),
  }
}

const minorCurrencySymbols: Record<string, string> = {
  AUD: 'c',
  CAD: '¢',
  CZK: 'h',
  DKK: 'øre',
  EUR: 'c',
  GBP: 'p',
  NOK: 'øre',
  PLN: 'gr',
  SEK: 'öre',
  USD: '¢',
}

const formatMinorCurrency = (
  amount: number,
  currency: string,
  notation: NumberNotation,
  locale: Intl.LocalesArgument | undefined,
  signDisplay: Intl.NumberFormatOptions['signDisplay'] | undefined,
) => {
  const symbol = minorCurrencySymbols[currency]
  if (!symbol || amount === 0 || Math.abs(amount) >= 1) {
    return null
  }

  const sign =
    new Intl.NumberFormat(locale ?? 'en-GB', {
      ...(signDisplay ? { signDisplay } : {}),
    })
      .formatToParts(amount)
      .find((part) => part.type === 'plusSign' || part.type === 'minusSign')?.value ?? ''

  const formattedAmount = formatNumberValue(
    Math.abs(amount) * 100,
    notation,
    'decimal',
    0,
    locale ?? 'en-GB',
    0,
  )
  return sign + formattedAmount + ' ' + symbol
}

export const formatMoney = (
  money: Partial<Money> | Partial<MoneyServer> | null | undefined,
  options?: MoneyFormatOptions,
) => formatMoneyValue(money, options)

export const formatMoneyMinor: typeof formatMoney = (money, options) =>
  formatMoneyValue(money, options, true)

const formatMoneyValue = (
  money: Partial<Money> | Partial<MoneyServer> | null | undefined,
  options?: MoneyFormatOptions,
  displaySubunit = false,
) => {
  const {
    notation = 'standard',
    maximumFractionDigits = 2,
    minimumFractionDigits = 2,
    currencyDisplay = 'narrowSymbol',
    signDisplay,
    locale,
    currencyLocale,
    allowZero = false,
  } = options || {}
  const amount = Number(money?.amount)
  if (!money?.currency || !Number.isFinite(amount) || (!amount && !allowZero)) {
    return '—'
  }

  if (displaySubunit) {
    const minorCurrency = formatMinorCurrency(amount, money.currency, notation, locale, signDisplay)
    if (minorCurrency) {
      return minorCurrency
    }
  }

  const resolvedNotation =
    notation === 'compact' && Math.abs(amount) >= 1000 ? 'compact' : 'standard'
  const resolvedMaximumFractionDigits = resolvedNotation === 'standard' ? 2 : maximumFractionDigits
  const resolvedMinimumFractionDigits = resolvedNotation === 'standard' ? 2 : minimumFractionDigits
  const numericAmount = formatNumberValue(
    Math.abs(amount),
    resolvedNotation,
    'decimal',
    resolvedMaximumFractionDigits,
    locale ?? 'en-GB',
    resolvedMinimumFractionDigits,
  )
  const { prefix, suffix } =
    formatMoneySymbolAffixes(options ?? {}, amount) ??
    formatMoneyAffixes(money.currency, currencyDisplay, locale, amount, signDisplay, currencyLocale)

  return `${prefix}${numericAmount}${suffix}`
}

export const getCurrencySymbol = (
  currency: string,
  locale: Intl.LocalesArgument = 'en-GB',
): string => {
  try {
    const parts = currencyParts(currency, 'narrowSymbol', locale)

    return parts.find((part) => part.type === 'currency')?.value ?? currency
  } catch {
    return currency
  }
}

const formatMoneySymbolAffixes = (options: MoneyFormatOptions, amount: number) => {
  if (!options.currencySymbol) {
    return null
  }

  const sign =
    new Intl.NumberFormat(options.locale ?? 'en-GB', {
      ...(options.signDisplay ? { signDisplay: options.signDisplay } : {}),
    })
      .formatToParts(amount)
      .find((part) => part.type === 'plusSign' || part.type === 'minusSign')?.value ?? ''

  return options.isSymbolBefore
    ? { prefix: sign + options.currencySymbol, suffix: '' }
    : { prefix: sign, suffix: ` ${options.currencySymbol}` }
}
